import React, { useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Popover,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import BlurOnIcon from "@mui/icons-material/BlurOn";
import HideImageOutlinedIcon from "@mui/icons-material/HideImageOutlined";
import WallpaperOutlinedIcon from "@mui/icons-material/WallpaperOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { VIDEO_BACKGROUND_PRESETS } from "./videoBackground";

function SwatchButton({ selected, title, onClick, children, disabled }) {
  return (
    <Tooltip title={title}>
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={onClick}
        aria-label={title}
        aria-pressed={selected}
        sx={{
          width: 56,
          height: 56,
          p: 0,
          border: selected ? "2px solid #fff" : "2px solid transparent",
          outline: selected ? "2px solid #8ab4f8" : "1px solid rgba(255,255,255,0.18)",
          outlineOffset: selected ? 1 : 0,
          borderRadius: 1.5,
          overflow: "hidden",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
          bgcolor: "#3c4043",
          color: "#e8eaed",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

/**
 * Seletor de fundo de câmera (nenhum / blur / presets / upload).
 */
export default function VideoBackgroundPicker({
  open,
  anchorEl,
  onClose,
  mode = "none",
  busy = false,
  supported = true,
  onSelectNone,
  onSelectBlur,
  onSelectPreset,
  onSelectCustomFile,
  dark = true,
  themePreset = null,
}) {
  const fileInputRef = useRef(null);
  const bg = dark ? "#292a2d" : "#fff";
  const fg = dark ? "#e8eaed" : "text.primary";
  const muted = dark ? "#9aa0a6" : "text.secondary";
  const presets = themePreset
    ? [themePreset, ...VIDEO_BACKGROUND_PRESETS.filter((p) => p.id !== themePreset.id)]
    : VIDEO_BACKGROUND_PRESETS;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onSelectCustomFile?.(file);
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={busy ? undefined : onClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: bg,
              color: fg,
              borderRadius: 2.5,
              p: 1.75,
              maxWidth: 320,
              boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
            },
          },
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
              Fundo da câmera
            </Typography>
            {busy ? <CircularProgress size={16} thickness={5} sx={{ color: "#8ab4f8" }} /> : null}
          </Stack>

          {!supported ? (
            <Typography sx={{ color: muted, fontSize: "0.82rem", lineHeight: 1.45 }}>
              Este navegador ou dispositivo não suporta efeitos de fundo. Tente Chrome ou Edge
              atualizados.
            </Typography>
          ) : (
            <Typography sx={{ color: muted, fontSize: "0.78rem", lineHeight: 1.4 }}>
              Desfoque ou substitua o fundo, como no Google Meet. O efeito vale para você e para
              quem está na chamada.
            </Typography>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <SwatchButton
              title="Sem efeito"
              selected={mode === "none"}
              disabled={busy || !supported}
              onClick={onSelectNone}
            >
              <HideImageOutlinedIcon fontSize="small" />
            </SwatchButton>
            <SwatchButton
              title="Desfoque"
              selected={mode === "blur"}
              disabled={busy || !supported}
              onClick={onSelectBlur}
            >
              <BlurOnIcon fontSize="small" />
            </SwatchButton>
            {presets.map((preset) => (
              <SwatchButton
                key={preset.id}
                title={preset.label}
                selected={mode === `preset:${preset.id}`}
                disabled={busy || !supported}
                onClick={() => onSelectPreset?.(preset)}
              >
                {preset.kind === "logo" ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundImage: `linear-gradient(135deg, ${preset.colors.join(", ")})`,
                      backgroundSize: "cover",
                      display: "grid",
                      placeItems: "center",
                      p: 0.5,
                    }}
                  >
                    <Box
                      component="img"
                      src={preset.logoSrc || "/brands/xpro/xvision-logo.png"}
                      alt=""
                      sx={{
                        width: "88%",
                        maxHeight: 22,
                        objectFit: "contain",
                        display: "block",
                        objectPosition: "left center",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundImage: `linear-gradient(135deg, ${(preset.colors || []).join(", ")})`,
                      position: "relative",
                    }}
                  >
                    {preset.kind === "theme" ? (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "rgba(0,0,0,0.18)",
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: 0.3,
                        }}
                      >
                        Tema
                      </Box>
                    ) : null}
                  </Box>
                )}
              </SwatchButton>
            ))}
            <SwatchButton
              title="Enviar imagem"
              selected={mode === "custom"}
              disabled={busy || !supported}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadFileOutlinedIcon fontSize="small" />
            </SwatchButton>
          </Stack>

          <Button
            size="small"
            onClick={onClose}
            disabled={busy}
            sx={{
              alignSelf: "flex-end",
              textTransform: "none",
              color: dark ? "#8ab4f8" : "primary.main",
              fontWeight: 600,
            }}
          >
            Fechar
          </Button>
        </Stack>
      </Popover>
    </>
  );
}

export function VideoBackgroundControlButton({
  active,
  title = "Fundo da câmera",
  onClick,
  disabled,
}) {
  return (
    <Tooltip title={title}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        <Box
          component="button"
          type="button"
          disabled={disabled}
          onClick={onClick}
          aria-label={title}
          aria-pressed={active}
          sx={{
            width: 48,
            height: 48,
            border: "none",
            borderRadius: "50%",
            cursor: disabled ? "default" : "pointer",
            bgcolor: active ? alpha("#8ab4f8", 0.35) : "rgba(255,255,255,0.12)",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            opacity: disabled ? 0.55 : 1,
            "&:hover": {
              bgcolor: disabled
                ? "rgba(255,255,255,0.12)"
                : active
                  ? alpha("#8ab4f8", 0.45)
                  : "rgba(255,255,255,0.2)",
            },
          }}
        >
          <WallpaperOutlinedIcon />
        </Box>
      </Box>
    </Tooltip>
  );
}
